import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Booking, BookingStatus, PayStatus } from '../mocks/bookings';

const dbStatusToMock: Record<string, BookingStatus> = {
  pending: 'Pending', confirmed: 'Confirmed', cancelled: 'Cancelled',
  completed: 'Completed', on_hold: 'On Hold',
};
const mockStatusToDb: Record<BookingStatus, string> = {
  Pending: 'pending', Confirmed: 'confirmed', Cancelled: 'cancelled',
  Completed: 'completed', 'On Hold': 'on_hold',
};
const dbPayToMock: Record<string, PayStatus> = {
  unpaid: 'Unpaid', partial: 'Partial', paid: 'Paid', refunded: 'Refunded',
};

function rowToBooking(r: Record<string, unknown>): Booking {
  return {
    id: (r.booking_ref as string) || (r.id as string),
    customer: '',
    customerAvatar: 'CU',
    customerPhone: '',
    customerEmail: '',
    service: (r.destination as string) || 'Travel Service',
    serviceType: 'Package',
    serviceIcon: 'ri-suitcase-line',
    destination: (r.destination as string) || '',
    travelDate: (r.travel_date as string) || '',
    returnDate: (r.return_date as string) || '',
    nights: 0,
    pax: ((r.pax_adults as number) || 1) + ((r.pax_children as number) || 0),
    status: dbStatusToMock[(r.status as string) || 'pending'] || 'Pending',
    totalAmount: (r.total_sell as number) || 0,
    paidAmount: 0,
    currency: (r.currency as string) || 'AED',
    payStatus: dbPayToMock[(r.payment_status as string) || 'unpaid'] || 'Unpaid',
    supplier: { name: '', type: '', contactPerson: '', phone: '', email: '', reference: '', costPrice: r.total_cost as number || 0, sellPrice: r.total_sell as number || 0 },
    payments: [],
    agent: 'You',
    agentAvatar: 'YO',
    createdAt: (r.created_at as string) || '',
    notes: (r.notes as string) || '',
  };
}

export function useBookings() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('bookings').select('*').order('created_at', { ascending: false });
    setBookings((data || []).map((r) => rowToBooking(r as Record<string, unknown>)));
    setLoading(false);
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const createBooking = async (booking: Partial<Booking>) => {
    const ref = `BK-${Date.now().toString().slice(-6)}`;
    const { data, error } = await supabase.from('bookings').insert({
      booking_ref: ref,
      destination: booking.destination,
      travel_date: booking.travelDate || null,
      return_date: booking.returnDate || null,
      pax_adults: booking.pax || 1,
      status: mockStatusToDb[booking.status || 'Pending'],
      total_cost: booking.supplier?.costPrice || 0,
      total_sell: booking.totalAmount || 0,
      currency: booking.currency || 'AED',
      payment_status: 'unpaid',
      notes: booking.notes,
    }).select().maybeSingle();
    if (!error && data) setBookings((prev) => [rowToBooking(data as Record<string, unknown>), ...prev]);
    return { data, error };
  };

  const updateBookingStatus = async (id: string, status: BookingStatus) => {
    const booking = bookings.find((b) => b.id === id);
    if (!booking) return;
    const { error } = await supabase.from('bookings').update({ status: mockStatusToDb[status] }).eq('booking_ref', id);
    if (!error) setBookings((prev) => prev.map((b) => b.id === id ? { ...b, status } : b));
  };

  return { bookings, loading, refetch: fetchBookings, createBooking, updateBookingStatus };
}
